import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api, { formatApiError } from './api';
import {
  TOKEN_KEY,
  VENDOR_KEY,
  isLocalPartnerType,
} from './constants';

const AuthContext = createContext(null);

function readToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
}

function readStoredVendor() {
  try {
    const raw = localStorage.getItem(VENDOR_KEY) || sessionStorage.getItem(VENDOR_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeAuth(nextToken, nextVendor, { rememberMe = true } = {}) {
  const store = rememberMe ? localStorage : sessionStorage;
  const other = rememberMe ? sessionStorage : localStorage;
  try {
    other.removeItem(TOKEN_KEY);
    other.removeItem(VENDOR_KEY);
    if (nextToken) store.setItem(TOKEN_KEY, nextToken);
    else {
      localStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_KEY);
    }
    if (nextVendor) store.setItem(VENDOR_KEY, JSON.stringify(nextVendor));
    else {
      localStorage.removeItem(VENDOR_KEY);
      sessionStorage.removeItem(VENDOR_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => readToken());
  const [vendor, setVendor] = useState(() => readStoredVendor());
  const [booting, setBooting] = useState(() => !!readToken());
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(() => {
    try {
      return localStorage.getItem('lp_remember_me_v1') !== '0';
    } catch {
      return true;
    }
  });

  const persist = useCallback((nextToken, nextVendor, opts) => {
    const remember = opts?.rememberMe ?? rememberMe;
    writeAuth(nextToken, nextVendor, { rememberMe: remember });
    setToken(nextToken || '');
    setVendor(nextVendor || null);
  }, [rememberMe]);

  const logout = useCallback(() => {
    persist('', null, { rememberMe: true });
  }, [persist]);

  const refreshShop = useCallback(async () => {
    const [settingsRes, productsRes] = await Promise.all([
      api.get('/vendor/auth/settings'),
      api.get('/api/vendor/products', { params: { include_inactive: true } }),
    ]);
    const settings = settingsRes.data || {};
    const productsMeta = productsRes.data || {};
    const vendorType = (productsMeta.vendor_type || settings.vendor_type || '').toLowerCase();
    const isLp =
      productsMeta.is_local_partner === true || isLocalPartnerType(vendorType);

    if (!isLp) {
      const err = new Error(
        'This account is not a local partner shop. Restaurants use partner.gosnel.com.'
      );
      err.code = 'NOT_LOCAL_PARTNER';
      throw err;
    }

    const next = {
      id: productsMeta.vendor_id || settings.id || null,
      name: settings.restaurant_name || 'Shop',
      email: settings.email || '',
      phone_e164: settings.phone_e164 || settings.phone || '',
      whatsapp_number: settings.whatsapp_number || settings.phone_e164 || settings.phone || '',
      address: settings.address || '',
      city: settings.city || '',
      country: settings.country || '',
      country_code: settings.country_code || 'AE',
      bank_currency: settings.bank_currency || 'AED',
      display_currency:
        settings.display_currency || settings.bank_currency || 'AED',
      delivery_fee_amount:
        settings.delivery_fee_amount != null ? Number(settings.delivery_fee_amount) : 0,
      delivery_fee_options: Array.isArray(settings.delivery_fee_options)
        ? settings.delivery_fee_options.map((n) => Number(n))
        : undefined,
      minimum_order_amount:
        settings.minimum_order_amount != null ? Number(settings.minimum_order_amount) : 0,
      open_time: settings.open_time || '09:00',
      close_time: settings.close_time || '21:00',
      delivery_radius_km: settings.delivery_radius_km ?? 0.5,
      logo_url: settings.logo_url || null,
      vendor_type: vendorType || 'other',
      accepting_orders: settings.accepting_orders !== false,
      delivery_available: settings.delivery_available !== false,
      neighbour_order_lang: (settings.neighbour_order_lang || 'en').toString().trim().toLowerCase() || 'en',
    };
    setVendor(next);
    writeAuth(readToken(), next, { rememberMe });
    return next;
  }, [rememberMe]);

  const loginWithToken = useCallback(
    async (accessToken, opts = {}) => {
      setError('');
      const access = String(accessToken || '').trim();
      if (!access) throw new Error('No token received');
      const remember = opts.rememberMe ?? rememberMe;
      setRememberMe(remember);
      writeAuth(access, null, { rememberMe: remember });
      setToken(access);
      try {
        return await refreshShop();
      } catch (err) {
        writeAuth('', null, { rememberMe: true });
        setToken('');
        throw err;
      }
    },
    [refreshShop, rememberMe]
  );

  const login = useCallback(
    async (email, password, opts = {}) => {
      setError('');
      const remember = opts.rememberMe !== false;
      setRememberMe(remember);
      const res = await api.post('/api/auth/login/vendor', {
        email: String(email || '').trim().toLowerCase(),
        password,
      });
      const access = res.data?.access_token || res.data?.token;
      if (!access) throw new Error('No token received');
      if (res.data?.approved === false) {
        throw new Error('Account pending approval. Contact GoSnel HQ.');
      }
      try {
        return await loginWithToken(access, { rememberMe: remember });
      } catch (err) {
        if (err?.code === 'NOT_LOCAL_PARTNER') {
          throw err;
        }
        throw new Error(formatApiError(err, 'Logged in but could not load shop profile'));
      }
    },
    [loginWithToken]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setBooting(false);
        return;
      }
      try {
        await refreshShop();
      } catch (err) {
        if (!cancelled) {
          if (err?.code === 'NOT_LOCAL_PARTNER' || err?.response?.status === 401) {
            logout();
          }
          setError(formatApiError(err, 'Session expired — please sign in again'));
        }
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps — boot once

  const value = useMemo(
    () => ({
      token,
      vendor,
      booting,
      error,
      setError,
      login,
      loginWithToken,
      logout,
      refreshShop,
      isAuthenticated: !!token,
    }),
    [token, vendor, booting, error, login, loginWithToken, logout, refreshShop]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
